/**
 * Définitions des genres musicaux — partagées entre la page d'accueil (vignettes
 * cliquables) et la page genre `/app/genre/[id]` (qui charge les morceaux du genre).
 *
 * Chaque genre a 3 facettes :
 *  - `hits`     : morceaux all-time les plus connus du genre
 *  - `releases` : nouveautés récentes (180 derniers jours)
 *  - `discover` : un mix plus large pour découvrir le genre
 *
 * Plus on a de queries par facette, plus on aura de candidats variés à mixer
 * (cible : ~50 morceaux après dédup + filtrage viewCount).
 */

export type Genre = {
  id: string
  label: string
  emoji: string
  gradient: string                // Tailwind from-X to-Y
  queries: {
    hits: string[]
    releases: string[]
    discover: string[]
  }
}

export const GENRES: Genre[] = [
  {
    id: 'pop',
    label: 'Pop',
    emoji: '🎤',
    gradient: 'from-pink-500 to-rose-600',
    queries: {
      hits: [
        'most popular pop song official',
        'best pop song all time official',
        'top pop hits billion views',
        'iconic pop song official video',
        'best pop ballad all time',
        'top pop diva official',
      ],
      releases: [
        'new pop song 2026 official',
        'pop new release 2026',
        'latest pop song official video',
        'new pop hit this month',
      ],
      discover: [
        'top pop hits 2025 2026',
        'best pop songs official audio',
        'pop indie hits',
        'pop synth hits',
        'pop rock fusion best',
      ],
    },
  },
  {
    id: 'rap',
    label: 'Hip-Hop',
    emoji: '🎧',
    gradient: 'from-orange-600 to-orange-800',
    queries: {
      hits: [
        'most popular hip hop song',
        'best rap song all time official',
        'top hip hop billion views',
        'classic hip hop song official',
        'iconic rap song official video',
        'best gangsta rap all time',
        'best trap song official',
      ],
      releases: [
        'new rap song 2026 official',
        'new hip hop release 2026',
        'new trap song this month',
        'latest rap hit official',
      ],
      discover: [
        'top hip hop 2026',
        'best rap songs official',
        'best drill song official',
        'underground hip hop hit',
        'east coast rap hits',
        'west coast rap classics',
      ],
    },
  },
  {
    id: 'rock',
    label: 'Rock',
    emoji: '🎸',
    gradient: 'from-red-500 to-orange-600',
    queries: {
      hits: [
        'best rock song all time',
        'classic rock hits official',
        'most popular rock song',
        'iconic rock song official video',
        'best metal song all time',
        'best hard rock all time',
        'best classic rock anthem',
      ],
      releases: [
        'new rock song 2026',
        'new rock release official',
        'latest rock hit 2026',
        'new metal song official',
      ],
      discover: [
        'top rock hits',
        'best rock songs official',
        'best alternative rock song',
        'best punk rock song',
        'best indie rock hit',
      ],
    },
  },
  {
    id: 'rnb',
    label: 'R&B',
    emoji: '💜',
    gradient: 'from-orange-600 to-orange-700',
    queries: {
      hits: [
        'best rnb song all time',
        'most popular rnb song official',
        'top rnb hits',
        'iconic rnb song official',
        'best soul song all time',
        'classic rnb song official',
      ],
      releases: [
        'new rnb song 2026',
        'new rnb release official',
        'latest rnb hit 2026',
        'new soul song official',
      ],
      discover: [
        'top rnb 2026',
        'best rnb songs official',
        'best neo soul song',
        'rnb slow jam best',
        'rnb modern hits',
      ],
    },
  },
  {
    id: 'elec',
    label: 'Electro',
    emoji: '🎛️',
    gradient: 'from-cyan-500 to-blue-700',
    queries: {
      hits: [
        'best electronic music all time',
        'most popular edm song',
        'top electronic hits',
        'iconic edm drop official',
        'best house song all time',
        'best techno song official',
        'best trance song all time',
      ],
      releases: [
        'new electronic song 2026',
        'new edm release official',
        'latest house song 2026',
        'new techno release',
      ],
      discover: [
        'top electronic 2026',
        'best edm hits',
        'best deep house song',
        'best progressive house',
        'best dubstep song',
        'best drum and bass song',
      ],
    },
  },
  {
    id: 'jazz',
    label: 'Jazz',
    emoji: '🎷',
    gradient: 'from-amber-500 to-orange-700',
    queries: {
      hits: [
        'best jazz song all time',
        'most popular jazz music',
        'classic jazz official',
        'iconic jazz standard',
        'best bebop song',
        'best smooth jazz hit',
      ],
      releases: [
        'new jazz song 2026',
        'new jazz release official',
        'latest jazz album 2026',
      ],
      discover: [
        'best jazz playlist',
        'top jazz songs',
        'best jazz fusion',
        'best jazz piano',
        'best vocal jazz',
      ],
    },
  },
  {
    id: 'reggae',
    label: 'Reggae',
    emoji: '🌴',
    gradient: 'from-emerald-500 to-green-700',
    queries: {
      hits: [
        'best reggae song all time',
        'most popular reggae music',
        'classic reggae bob marley',
        'iconic reggae song official',
        'best dancehall all time',
        'best roots reggae song',
      ],
      releases: [
        'new reggae song 2026',
        'new dancehall release',
        'latest dancehall hit 2026',
        'new reggae 2026 official',
      ],
      discover: [
        'top reggae hits',
        'best dancehall songs',
        'best dub reggae song',
        'best caribbean song hit',
        'best zouk song hit',
      ],
    },
  },
  {
    id: 'latin',
    label: 'Latino',
    emoji: '🌶️',
    gradient: 'from-red-500 to-pink-600',
    queries: {
      hits: [
        'most popular reggaeton all time',
        'best latin song billion views',
        'top latin hits official',
        'iconic reggaeton song',
        'best bachata song all time',
        'best salsa song hit',
        'best latin pop song',
      ],
      releases: [
        'new reggaeton 2026 oficial',
        'nuevo latin song 2026',
        'nuevo reggaeton hit',
        'latin new release official',
      ],
      discover: [
        'top latin 2026',
        'best reggaeton hits',
        'best urbano latino song',
        'best merengue song',
        'best cumbia song',
      ],
    },
  },
  {
    id: 'cls',
    label: 'Classique',
    emoji: '🎻',
    gradient: 'from-indigo-700 to-slate-800',
    queries: {
      hits: [
        'most popular classical music',
        'best classical pieces',
        'famous classical music',
        'beethoven best piece',
        'mozart best piece',
        'chopin best piece',
      ],
      releases: [
        'new classical recording 2026',
        'modern classical music 2026',
        'latest classical performance',
      ],
      discover: [
        'classical music best',
        'most beautiful classical music',
        'best baroque music',
        'best romantic classical',
        'best film score classical',
      ],
    },
  },
  {
    id: 'fr',
    label: 'Variété FR',
    emoji: '🇫🇷',
    gradient: 'from-blue-600 to-red-600',
    queries: {
      hits: [
        'meilleur clip rap français',
        'top rap fr most viewed',
        'meilleur son français',
        'meilleur chanson française',
        'meilleur clip variété française',
        'classique chanson française',
        'meilleur rap fr clip officiel',
      ],
      releases: [
        'nouveau son rap français 2026',
        'nouveauté musique francophone',
        'nouveau clip rap fr',
        'sortie rap fr 2026',
        'nouvelle chanson française 2026',
      ],
      discover: [
        'top rap français',
        'variété française hits',
        'meilleur rap fr underground',
        'chanteur français célèbre',
        'meilleur duo rap français',
      ],
    },
  },
  {
    id: 'kpop',
    label: 'K-Pop',
    emoji: '✨',
    gradient: 'from-pink-400 to-orange-600',
    queries: {
      hits: [
        'best kpop most viewed',
        'most popular kpop song',
        'top kpop hits all time',
        'iconic kpop song official',
        'bts most popular song',
        'blackpink most popular song',
        'best kpop boy group song',
        'best kpop girl group song',
      ],
      releases: [
        'new kpop song 2026 official',
        'kpop new release 2026',
        'latest kpop comeback',
        'new kpop mv 2026',
      ],
      discover: [
        'top kpop 2026',
        'best kpop songs',
        'best kpop debut song',
        'best 4th gen kpop',
        'best kpop ballad',
      ],
    },
  },
  {
    id: 'chill',
    label: 'Chill',
    emoji: '🌙',
    gradient: 'from-slate-700 to-indigo-900',
    queries: {
      hits: [
        'most popular chill lofi song',
        'best chill music',
        'top lofi hits',
        'best chill ambient',
        'most viewed lofi',
        'best relaxing music hit',
      ],
      releases: [
        'new chill song 2026',
        'new lofi release 2026',
        'latest chill hit',
      ],
      discover: [
        'lofi chill mix',
        'best chill songs',
        'best ambient music',
        'best acoustic chill',
        'best downtempo song',
      ],
    },
  },
]

export function findGenre(id: string): Genre | undefined {
  return GENRES.find(g => g.id === id)
}
